import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';

import { includesConditionOptions } from 'constants/automation';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import { useCaseMatter } from 'hooks/useCaseMatter';

const CaseMatterFilter = ({ index, practiceAreaId = [], caseMatterOptions }) => {
  const { caseMatterList, isCaseMatterLoading } = useCaseMatter(true, practiceAreaId);
  const {
    formState: { errors },
    control,
    getValues,
    setValue,
  } = useFormContext();

  const isFirstRender = useRef(true);
  const [optionsVersion, setOptionsVersion] = useState(0);
  const [listVersion, setListVersion] = useState(0);
  const prevCaseMatterOptions = useRef(caseMatterOptions);
  const prevCaseMatterList = useRef(caseMatterList);

  useEffect(() => {
    if (caseMatterOptions !== prevCaseMatterOptions.current) {
      if (caseMatterOptions !== undefined && caseMatterOptions !== null && Array.isArray(caseMatterOptions)) {
        setOptionsVersion((prev) => prev + 1);
      }
      prevCaseMatterOptions.current = caseMatterOptions;
    }
  }, [caseMatterOptions]);

  useEffect(() => {
    if (caseMatterList !== prevCaseMatterList.current) {
      if (caseMatterList !== undefined && caseMatterList !== null && Array.isArray(caseMatterList)) {
        setListVersion((prev) => prev + 1);
      }
      prevCaseMatterList.current = caseMatterList;
    }
  }, [caseMatterList]);

  const options = useMemo(() => {
    const optionsHasUpdates = optionsVersion > 0;
    const listHasUpdates = listVersion > 0;

    if (optionsHasUpdates && listHasUpdates) {
      return optionsVersion >= listVersion ? caseMatterOptions || [] : caseMatterList || [];
    } else if (optionsHasUpdates) {
      return caseMatterOptions || [];
    } else if (listHasUpdates) {
      return caseMatterList || [];
    }
    return caseMatterList || [];
  }, [caseMatterOptions, caseMatterList, optionsVersion, listVersion]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const currentValue = getValues(`filters.${index}.value`);
    const selectedValues = currentValue?.['case_description.case_matter'] || [];

    if (selectedValues.length > 0 && options.length > 0) {
      const validOptionIds = new Set(options.map((option) => option.case_matter_id));
      const validValues = selectedValues.filter((value) => validOptionIds.has(value));

      if (validValues.length !== selectedValues.length) {
        setValue(`filters.${index}.value`, { 'case_description.case_matter': validValues }, { shouldValidate: true });
      }
    }
    if (options.length === 0) {
      setValue(`filters.${index}.value`, { 'case_description.case_matter': [] }, { shouldValidate: true });
    }
  }, [options, index, getValues, setValue]);
  return (
    <>
      <div className="me-2 filterbar-condition">
        <Controller
          name={`filters.${index}.condition`}
          control={control}
          rules={{ required: 'Condition is required' }}
          render={({ field }) => (
            <Dropdown
              options={includesConditionOptions}
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              placeholder="Select Condition"
              className="w-100"
              inputRef={field.ref}
            />
          )}
        />
        <RequiredValidation field={`filters.${index}.condition`} errors={errors} />
      </div>
      <div className="me-2 filterbar-value">
        <Controller
          name={`filters.${index}.value`}
          control={control}
          rules={{ required: 'Value is required' }}
          render={({ field }) => (
            <MultiSelect
              options={options}
              value={(field.value?.['case_description.case_matter'] || []).filter((selected) =>
                options.some((option) => option.case_matter_id === selected)
              )}
              onChange={(e) => field.onChange({ 'case_description.case_matter': e.value })}
              placeholder="Select Value"
              isFilter={true}
              className="input-shadow w-100"
              display="chip"
              optionLabel="matter_name"
              optionValue="case_matter_id"
              dropdownIcon={isCaseMatterLoading ? 'pi pi-spin pi-spinner' : undefined}
            />
          )}
        />
        <RequiredValidation field={`filters.${index}.value`} errors={errors} />
      </div>
    </>
  );
};

export default CaseMatterFilter;
